import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { signInWithPopup, onAuthStateChanged } from 'firebase/auth';
import { auth, googleProvider } from '../../firebase/firebase';
import NotificationPopup from '../../avatarSystem/notificationSystem/NotificationPopup';

import { 
  Home, UserCircle, Settings, Crown,
  Bell, ChevronLeft, ChevronRight, Star, Heart, Zap, LogOut, LogIn,
  MessageSquarePlus, ChevronUp, ChevronDown
} from 'lucide-react';
import './sidebar2.css';

// Custom SVG Icons
const HomeIcon = ({ size = 18 }) => (
<svg class="w-6 h-6 text-gray-800 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24">
  <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M14 17h6m-3 3v-6M4.857 4h4.286c.473 0 .857.384.857.857v4.286a.857.857 0 0 1-.857.857H4.857A.857.857 0 0 1 4 9.143V4.857C4 4.384 4.384 4 4.857 4Zm10 0h4.286c.473 0 .857.384.857.857v4.286a.857.857 0 0 1-.857.857h-4.286A.857.857 0 0 1 14 9.143V4.857c0-.473.384-.857.857-.857Zm-10 10h4.286c.473 0 .857.384.857.857v4.286a.857.857 0 0 1-.857.857H4.857A.857.857 0 0 1 4 19.143v-4.286c0-.473.384-.857.857-.857Z"/>
</svg>
);

const ExploreIcon = ({ size = 18 }) => (
<svg className="w-6 h-6 text-gray-800 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24">
  <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="m13 19 3.5-9 3.5 9m-6.125-2h5.25M3 7h7m0 0h2m-2 0c0 1.63-.793 3.926-2.239 5.655M7.5 6.818V5m.261 7.655C6.79 13.82 5.521 14.725 4 15m3.761-2.345L5 10m2.761 2.655L10.2 15"/>
</svg>
);

const FavoritesIcon = ({ size = 18 }) => (
<svg class="w-6 h-6 text-gray-800 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
  <path fill-rule="evenodd" d="M5 4a2 2 0 0 0-2 2v1h10.968l-1.9-2.28A2 2 0 0 0 10.532 4H5ZM3 19V9h18v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Zm9-8.5a1 1 0 0 1 1 1V13h1.5a1 1 0 1 1 0 2H13v1.5a1 1 0 1 1-2 0V15H9.5a1 1 0 1 1 0-2H11v-1.5a1 1 0 0 1 1-1Z" clip-rule="evenodd"/>
</svg>
);

const MyWorkIcon = ({ size = 18 }) => (
<svg class="w-6 h-6 text-gray-800 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
  <path fill-rule="evenodd" d="M12 6a3.5 3.5 0 1 0 0 7 3.5 3.5 0 0 0 0-7Zm-1.5 8a4 4 0 0 0-4 4 2 2 0 0 0 2 2h7a2 2 0 0 0 2-2 4 4 0 0 0-4-4h-3Zm6.82-3.096a5.51 5.51 0 0 0-2.797-6.293 3.5 3.5 0 1 1 2.796 6.292ZM19.5 18h.5a2 2 0 0 0 2-2 4 4 0 0 0-4-4h-1.1a5.503 5.503 0 0 1-.471.762A5.998 5.998 0 0 1 19.5 18ZM4 7.5a3.5 3.5 0 0 1 5.477-2.889 5.5 5.5 0 0 0-2.796 6.293A3.501 3.501 0 0 1 4 7.5ZM7.1 12H6a4 4 0 0 0-4 4 2 2 0 0 0 2 2h.5a5.998 5.998 0 0 1 3.071-5.238A5.505 5.505 0 0 1 7.1 12Z" clip-rule="evenodd"/>
</svg>
);

const Sidebar2 = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [isExpanded, setIsExpanded] = useState(true);
  const [isNavVisible, setIsNavVisible] = useState(true); // New state for up/down visibility
  const [activeTab, setActiveTab] = useState('explore');
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showCreateDropdown, setShowCreateDropdown] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [screenSize, setScreenSize] = useState('desktop');
  const [user, setUser] = useState(null);
  const [profilePhotoError, setProfilePhotoError] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  
  // Special case for notification item which isn't a real link
  const NotificationItem = ({ icon, text, isActive = false, badge = null, isCollapsed = false, onClick }) => {
    return (
      <div className={`nav-item ${isActive ? 'active' : ''}`} onClick={onClick}>
        <span className="nav-icon">{icon}</span>
        {!isCollapsed && (
          <>
            <span className="nav-text">{text}</span>
            {badge && <span className="nav-badge">{badge}</span>}
          </>
        )}
      </div>
    );
  };
  
  const dropdownRef = useRef(null);
  const createDropdownRef = useRef(null);

  // Update active tab based on current route
  useEffect(() => {
    const currentPath = location.pathname;
    if (currentPath === '/chat') {
      setActiveTab('chat buddy');
    } else if (currentPath.startsWith('/explore')) {
      setActiveTab('explore');
    } else if (currentPath.startsWith('/avatars')) {
      setActiveTab('avatars');
    }
  }, [location.pathname]);

  const navItems = [
    { id: 'chat buddy', label: 'Chat Buddy', icon: HomeIcon, route: '/' },
    { id: 'explore', label: 'Explore', icon: ExploreIcon, hasDropdown: true },
    { id: 'avatars', label: 'Avatars', icon: FavoritesIcon, route: '/avatars' },
  ];

  const createOptions = [
    { id: 'browse', label: 'Browse Templates', icon: Star, route: '/explore/templates' },
  ];

  // Enhanced notifications with more realistic data
  const [notifications, setNotifications] = useState([
    { 
      id: 1, 
      title: 'New AI Feature Available', 
      desc: 'Enhanced scenario generation is now live', 
      time: '2m ago', 
      unread: true,
      type: 'feature'
    },
    { 
      id: 2, 
      title: 'Scenario Completed', 
      desc: 'Your roleplay scenario "Medieval Quest" is ready', 
      time: '15m ago', 
      unread: true,
      type: 'success'
    },
    { 
      id: 3, 
      title: 'Community Update', 
      desc: 'New scenarios shared by the community', 
      time: '1h ago', 
      unread: false,
      type: 'info'
    },
    { 
      id: 4, 
      title: 'Storage Warning', 
      desc: '85% of your storage is being used', 
      time: '3h ago', 
      unread: false,
      type: 'warning'
    },
    { 
      id: 5, 
      title: 'Weekly Summary', 
      desc: 'You created 3 scenarios this week', 
      time: '1d ago', 
      unread: false,
      type: 'info'
    }
  ]);

  // Firebase auth state tracking
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        console.log("ModernNavigation: User authenticated:", currentUser);
        console.log("ModernNavigation: Photo URL:", currentUser.photoURL);
        setUser(currentUser);
        setProfilePhotoError(false);
      } else {
        setUser(null);
      }
    });
    
    return () => unsubscribe();
  }, []);

  // Sidebar width adjustment effect
  useEffect(() => {
    const updateSidebarWidth = () => {
      const mainSidebar = document.querySelector('.scenario-sidebar');
      
      if (mainSidebar) {
        const isCollapsed = mainSidebar.classList.contains('collapsed');
        const sidebarWidth = isCollapsed ? '60px' : '250px';
        
        document.documentElement.style.setProperty('--sidebar-width', sidebarWidth);
        
        const navWrapper = document.querySelector('.nav-wrapper');
        if (navWrapper) {
          if (isCollapsed) {
            navWrapper.classList.add('sidebar-collapsed');
          } else {
            navWrapper.classList.remove('sidebar-collapsed');
          }
        }
      }
    };

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          updateSidebarWidth();
        }
      });
    });

    const mainSidebar = document.querySelector('.scenario-sidebar');
    if (mainSidebar) {
      observer.observe(mainSidebar, {
        attributes: true,
        attributeFilter: ['class']
      });
      
      updateSidebarWidth();
    }

    return () => {
      observer.disconnect();
    };
  }, []);
// In your React component, make sure the sidebar state detection is more reliable
useEffect(() => {
  const updateSidebarState = () => {
    const mainSidebar = document.querySelector('.scenario-sidebar');
    const navWrapper = document.querySelector('.nav-wrapper');
    
    if (mainSidebar && navWrapper) {
      const isCollapsed = mainSidebar.classList.contains('collapsed');
      
      // Force update the nav wrapper class immediately
      if (isCollapsed) {
        navWrapper.classList.add('sidebar-collapsed');
      } else {
        navWrapper.classList.remove('sidebar-collapsed');
      }
      
      // Also update CSS custom property
      document.documentElement.style.setProperty('--sidebar-width', isCollapsed ? '60px' : '250px');
    }
  };

  // Run immediately
  updateSidebarState();

  // Set up observer for changes
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
        updateSidebarState();
      }
    });
  });

  const mainSidebar = document.querySelector('.scenario-sidebar');
  if (mainSidebar) {
    observer.observe(mainSidebar, {
      attributes: true,
      attributeFilter: ['class']
    });
  }

  // Also listen for window resize to handle edge cases
  window.addEventListener('resize', updateSidebarState);

  return () => {
    observer.disconnect();
    window.removeEventListener('resize', updateSidebarState);
  };
}, []);
  // Handle responsive behavior and screen size detection
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      
      if (width < 480) {
        setScreenSize('mobile');
        setIsMobile(true);
        setIsExpanded(false);
      } else if (width < 640) {
        setScreenSize('mobile-landscape');
        setIsMobile(true);
        setIsExpanded(false);
      } else if (width < 768) {
        setScreenSize('tablet-portrait');
        setIsMobile(true);
        setIsExpanded(false);
      } else if (width < 1024) {
        setScreenSize('tablet-landscape');
        setIsMobile(false);
        setIsExpanded(true);
      } else if (width < 1440) {
        setScreenSize('desktop');
        setIsMobile(false);
        setIsExpanded(true);
      } else {
        setScreenSize('large-desktop');
        setIsMobile(false);
        setIsExpanded(true);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle click outside for dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false);
        setShowNotifications(false);
      }
      if (createDropdownRef.current && !createDropdownRef.current.contains(event.target)) {
        setShowCreateDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeAllDropdowns();
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const closeAllDropdowns = () => {
    setShowUserDropdown(false);
    setShowNotifications(false);
    setShowCreateDropdown(false);
  };

  // Handle navigation
  const handleNavigation = (route) => {
    navigate(route);
    closeAllDropdowns();
  };

  // Handle logo click - navigate to home
  const handleLogoClick = () => {
    navigate('/');
    closeAllDropdowns();
  };
  
  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  const toggleUserDropdown = () => {
    setShowUserDropdown(!showUserDropdown);
  };

  const toggleNotifications = (e) => {
    e.preventDefault();
    setShowNotifications(!showNotifications);
  };
  
  const handleCloseNotifications = () => {
    setShowNotifications(false);
  };

  const handleTabClick = (item) => {
    if (item.hasDropdown) {
      setShowCreateDropdown(!showCreateDropdown);
      setShowUserDropdown(false);
      setShowNotifications(false);
    } else if (item.route) {
      handleNavigation(item.route);
    }
  };

  const handleExpandToggle = () => {
    if (!isMobile) {
      setIsExpanded(!isExpanded);
      closeAllDropdowns();
    }
  };

  // New function to handle up/down toggle
  const handleNavVisibilityToggle = () => {
    setIsNavVisible(!isNavVisible);
    closeAllDropdowns();
  };

  // Firebase login handler
  const handleLogin = async () => {
    try {
      googleProvider.setCustomParameters({
        prompt: 'select_account',
        scope: 'profile email'
      });
      
      const result = await signInWithPopup(auth, googleProvider);
      console.log("ModernNavigation: Sign-in successful:", result);
      console.log("ModernNavigation: User photo URL:", result.user.photoURL);
      
      setUser(result.user);
      setProfilePhotoError(false);
      closeAllDropdowns();
    } catch (error) {
      console.error("Google Sign-in Error:", error);
    }
  };

  // Firebase logout handler
  const handleLogout = async () => {
    try {
      await auth.signOut();
      setUser(null);
      closeAllDropdowns();
      navigate('/');
    } catch (error) {
      console.error("Logout Error:", error);
    }
  };

  // Handle user menu actions
  const handleProfileClick = () => {
    handleNavigation('/profile');
  };

  const handleSettingsClick = () => {
    handleNavigation('/settings');
  };

  const handleUpgradeClick = () => {
    handleNavigation('/upgrade');
  };

  // Handle notification actions
  const handleNotificationClick = (notificationId) => {
    setNotifications(prev => 
      prev.map(notif => 
        notif.id === notificationId 
          ? { ...notif, unread: false }
          : notif
      )
    );
    console.log('Notification clicked:', notificationId);
  };

  const markAllNotificationsRead = () => {
    setNotifications(prev => 
      prev.map(notif => ({ ...notif, unread: false }))
    );
  };

  // Image error handler
  const handleImageError = () => {
    console.error("ModernNavigation: Failed to load profile image");
    setProfilePhotoError(true);
  };

  const shouldShowText = isExpanded && !isMobile && screenSize !== 'mobile' && screenSize !== 'mobile-landscape';
  const shouldShowBrand = isExpanded && !isMobile && screenSize !== 'tablet-portrait';
  const unreadCount = notifications.filter(n => n.unread).length;

  return (
    <div className="app-container">
      {/* Navigation */}
      <div className={`nav-wrapper ${isNavVisible ? 'nav-visible' : 'nav-hidden'}`}>
        <div className={`nav-container ${isExpanded ? 'expanded' : 'collapsed'}`}>
          <div className="nav-glass">
            <div className="nav-content">
              
              {/* Nav Tabs */}
              <div className="nav-tabs">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <div key={item.id} className="nav-tab-wrapper" ref={item.id === 'explore' ? createDropdownRef : null}>
                      <button
                        onClick={() => handleTabClick(item)}
                        className={`nav-tab ${activeTab === item.id ? 'active' : ''} ${shouldShowText ? 'expanded' : 'collapsed'}`}
                        aria-label={item.label}
                      >
                        <span className={`tab-icon ${shouldShowText ? 'expanded' : 'collapsed'}`}>
                          <Icon size={18} />
                        </span>
                        <span className={`tab-text ${shouldShowText ? 'expanded' : 'collapsed'}`}>
                          {item.label}
                        </span>
                        {item.badge && (
                          <span className={`tab-badge ${shouldShowText ? 'expanded' : 'collapsed'}`}>
                            {item.badge}
                          </span>
                        )}
                      </button>

                      {/* Create/Explore Dropdown */}
                      {item.hasDropdown && showCreateDropdown && (
                        <div className="dropdown dropdown-create">
                          <div className="dropdown-header">
                            <div className="dropdown-title">Explore</div>
                            <div className="dropdown-subtitle">Discover and create</div>
                          </div>
                          <div className="dropdown-content">
                            {createOptions.map((option) => {
                              const OptionIcon = option.icon;
                              return (
                                <button
                                  key={option.id}
                                  onClick={() => handleNavigation(option.route)}
                                  className="dropdown-item"
                                >
                                  <span className="dropdown-item-icon">
                                    <OptionIcon size={16} />
                                  </span>
                                  <span className="dropdown-item-title">{option.label}</span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Controls */}
              <div className="nav-controls">
                {/* Notifications */}
                <div className="nav-tab-wrapper" ref={dropdownRef}>
                  <button
                    onClick={() => {
                      setShowNotifications(!showNotifications);
                      setShowUserDropdown(false);
                      setShowCreateDropdown(false);
                    }}
                    className="control-btn"
                    aria-label="Notifications"
                  >
                    <svg className="w-6 h-6 text-gray-800 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                      <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m10.827 5.465-.435-2.324m.435 2.324a5.338 5.338 0 0 1 6.033 4.333l.331 1.769c.44 2.345 2.383 2.588 2.6 3.761.11.586.22 1.171-.31 1.271l-12.7 2.377c-.529.099-.639-.488-.749-1.074C5.813 16.73 7.538 15.8 7.1 13.455c-.219-1.169.218 1.162-.33-1.769a5.338 5.338 0 0 1 4.058-6.221Zm-7.046 4.41c.143-1.877.822-3.461 2.086-4.856m2.646 13.633a3.472 3.472 0 0 0 6.728-.777l.09-.5-6.818 1.277Z"/>
                    </svg>
                    {unreadCount > 0 && (
                      <div className="notification-dot">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </div>
                    )}
                  </button>
                </div>

                {/* Up/Down Toggle */}
                <button
                  onClick={handleNavVisibilityToggle}
                  className="control-btn"
                  aria-label={isNavVisible ? 'Hide navigation' : 'Show navigation'}
                >
                  {isNavVisible ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                </button>

                {/* Expand/Collapse - Hide on mobile */}
                {!isMobile && (
                  <button
                    onClick={handleExpandToggle}
                    className="control-btn"
                    aria-label={isExpanded ? 'Collapse navigation' : 'Expand navigation'}
                  >
                    {isExpanded ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Show Down Arrow when Navigation is Hidden */}
      {!isNavVisible && (
        <div className="nav-show-indicator">
          <button
            onClick={handleNavVisibilityToggle}
            className="show-nav-btn"
            aria-label="Show navigation"
          >
            <ChevronDown size={20} />
          </button>
        </div>
      )}

      {/* Notification popup */}
      <NotificationPopup
        isOpen={showNotifications}
        onClose={handleCloseNotifications}
      />
    </div>
  );
};

export default Sidebar2;